import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { comics, genres, type Comic } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";
import SectionHeading from "@/components/feature/SectionHeading";

const heroChips = ["恋爱", "奇幻", "校园", "都市", "复仇", "悬疑"];

function Hero() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/ranking?q=${encodeURIComponent(q)}` : "/ranking");
  };
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20Korean%20webtoon%20collage%20background%20with%20abstract%20comic%20panels%2C%20cherry%20blossoms%2C%20book%20and%20sparkles%20floating%2C%20warm%20peach%20coral%20and%20cream%20color%20palette%2C%20soft%20pastel%20artistic%20style%2C%20cozy%20and%20youthful%20mood%2C%20high%20detail%20illustration&width=1600&height=900&seq=hero-home&orientation=landscape"
          alt="免费韩国漫画聚合阅读站背景图"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
      </div>

      <div className="relative mx-auto flex h-[560px] w-full max-w-5xl flex-col items-center justify-center px-4 text-center md:h-[640px]">
        <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-background-50/40 bg-background-50/10 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur">
          <i className="ri-sparkling-2-fill text-secondary-300"></i>
          韩漫聚合阅读 · 持续更新
        </span>
        <h1 className="max-w-3xl font-display text-3xl leading-tight text-background-50 md:text-5xl md:leading-tight">
          免费韩国漫画<span className="text-secondary-300">排行榜</span>与完结推荐
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-7 text-background-100 md:text-base">
          恋爱、奇幻、校园、都市等题材一站聚合。按排行榜与题材精准筛选，热门冷门全覆盖，帮你快速找到想看的韩漫。
        </p>

        <form onSubmit={submit} className="mt-8 flex w-full max-w-xl items-center overflow-hidden rounded-full bg-background-50 p-1.5 shadow-sm">
          <i className="ri-search-line pl-4 text-lg text-foreground-400"></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索你想看的韩漫名称或题材"
            aria-label="搜索韩漫"
            className="h-10 flex-1 bg-transparent px-3 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            立即搜索
          </button>
        </form>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {heroChips.map((g) => (
            <Link
              key={g}
              to={`/ranking?genre=${encodeURIComponent(g)}`}
              className="whitespace-nowrap rounded-full border border-background-50/40 px-4 py-1.5 text-xs text-background-50 transition-colors hover:bg-primary-500 hover:border-primary-500"
            >
              {g}
            </Link>
          ))}
        </div>

        <div className="mt-10 grid w-full max-w-lg grid-cols-3 gap-4 border-t border-background-50/30 pt-6">
          {[
            { num: "1200+", label: "精选韩漫" },
            { num: "8+", label: "热门题材" },
            { num: "每日更新", label: "追更不停" },
          ].map((s) => (
            <div key={s.label}>
              <p className="font-display text-xl text-background-50 md:text-2xl">{s.num}</p>
              <p className="mt-1 text-xs text-background-200">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotRanking({ top }: { top: Comic[] }) {
  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <SectionHeading
        eyebrow="Hot Ranking"
        title="韩漫排行榜"
        desc="根据评分与人气综合排名的本周热榜，热门与冷门佳作都在这里。"
        link={{ label: "查看完整榜单", to: "/ranking" }}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {top.map((c, i) => (
          <Link
            key={c.id}
            to={`/comic/${c.id}`}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-background-100">
              <img
                src={c.cover}
                alt={`${c.title} 韩漫排行第${i + 1}名`}
                loading="lazy"
                className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              />
              <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg bg-primary-500 font-display text-sm text-background-50">
                {i + 1}
              </span>
            </div>
            <div className="flex flex-1 flex-col p-3">
              <h3 className="line-clamp-1 text-sm font-bold text-foreground-950 group-hover:text-primary-500">{c.title}</h3>
              <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{c.genres.join(" · ")}</p>
              <div className="mt-2 flex items-center gap-1 text-xs">
                <i className="ri-star-fill text-secondary-500"></i>
                <span className="font-bold text-foreground-900">{c.rating.toFixed(1)}</span>
                <span className="ml-auto flex items-center gap-1 text-foreground-500">
                  <i className="ri-eye-line"></i>
                  {c.views}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Genres() {
  return (
    <section className="bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="Genre"
          title="按题材分类浏览"
          desc="恋爱、奇幻、校园、都市、复仇、悬疑……六大热门题材，按你的口味精准挑选韩漫。"
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {genres.map((g) => (
            <Link
              key={g.name}
              to={`/ranking?genre=${encodeURIComponent(g.name)}`}
              className="group relative flex h-40 items-end overflow-hidden rounded-2xl p-5"
            >
              <img
                src={g.cover}
                alt={`${g.name}题材免费韩国漫画`}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
              <div className="relative flex w-full items-center justify-between">
                <div>
                  <h3 className="font-display text-xl text-background-50">{g.name}</h3>
                  <p className="mt-1 text-xs text-background-200">{g.desc}</p>
                </div>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50 opacity-0 transition-opacity group-hover:opacity-100">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Latest({ list }: { list: Comic[] }) {
  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <SectionHeading
        eyebrow="Latest"
        title="最近更新"
        desc="追更不停歇，这里收录了近期更新最快的热门韩漫，抢先看新剧情。"
        link={{ label: "查看更多", to: "/ranking" }}
      />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {list.map((c) => (
          <ComicCard key={c.id} comic={c} />
        ))}
      </div>
    </section>
  );
}

function Completed() {
  const done = comics.filter((c) => c.status === "完结");
  return (
    <section className="bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="Completed"
          title="完结作品推荐"
          desc="一口气看到结局，追番不再等待。精选高分已完结韩漫，放心开坑不踩雷。"
          link={{ label: "全部完结作品", to: "/ranking?filter=completed" }}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {done.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: "免费韩国漫画网站是免费的吗？",
    a: "是的，本聚合站收录的漫画均提供免费阅读与榜单推荐，方便大家快速发现感兴趣的题材。部分内容由作者授权展示，具体以页面说明为准。",
  },
  {
    q: "如何根据题材快速找到想看的韩漫？",
    a: "你可以在首页的题材分类或排行榜页中，按恋爱、奇幻、校园、都市、复仇、悬疑等题材进行筛选，也可直接搜索漫画名称精准定位。",
  },
  {
    q: "排行榜的排名依据是什么？",
    a: "排行榜综合了作品的评分、人气与近期更新热度，按周更新。完结推荐则精选已完结的高分佳作，方便一口气追完。",
  },
  {
    q: "网站多久更新一次新作品？",
    a: "我们持续追踪热门韩漫的更新动态，首页的最近更新板块会及时收录新章与新作，尽量保证你不错过任何精彩内容。",
  },
];

function Faq() {
  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <SectionHeading
        eyebrow="FAQ"
        title="常见问题"
        desc="关于免费韩国漫画阅读、题材筛选与榜单的常见疑问，这里都有解答。"
      />
      <div className="grid gap-4 md:grid-cols-2">
        {faqs.map((f) => (
          <details
            key={f.q}
            className="group rounded-2xl border border-background-200/70 bg-background-50 p-5"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950 [&::-webkit-details-marker]:hidden">
              {f.q}
              <i className="ri-add-line shrink-0 text-lg text-primary-500 transition-transform group-open:rotate-45"></i>
            </summary>
            <p className="mt-3 text-sm leading-6 text-foreground-600">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function Brand() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20peach%20coral%20and%20cream%20waves%2C%20floating%20books%20and%20stars%2C%20Korean%20webtoon%20style%20art%2C%20cozy%20youthful%20atmosphere%2C%20high%20detail%20illustration&width=1600&height=700&seq=brand-cta&orientation=landscape"
          alt="韩漫悦读品牌介绍背景"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/35 to-black/30"></div>
      </div>
      <div className="relative mx-auto flex w-full max-w-7xl flex-col items-start px-4 py-16 md:px-6 md:py-24">
        <p className="text-xs font-bold uppercase tracking-widest text-secondary-300">About Us</p>
        <h2 className="mt-3 max-w-2xl font-display text-2xl leading-snug text-background-50 md:text-4xl md:leading-snug">
          一站聚合，读懂每一部韩漫的精彩
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-7 text-background-100">
          韩漫悦读专注免费韩国漫画的聚合推荐。我们用心梳理排行榜与完结作品，把恋爱、奇幻、校园、都市等题材分类呈现给你，让找漫画、选漫画、看漫画都变得简单。
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/ranking"
            className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            开始探索
          </Link>
          <Link
            to="/about"
            className="whitespace-nowrap rounded-full border border-background-50/50 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
          >
            了解我们
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const ranked = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 5);
  const latest = [...comics].sort((a, b) => b.episodes - a.episodes).slice(0, 12);
  return (
    <>
      <Hero />
      <HotRanking top={ranked} />
      <Genres />
      <Latest list={latest} />
      <Completed />
      <Faq />
      <Brand />
    </>
  );
}